// The actions at the bar's right (2026-10-14): Puzzle, when the reader's
// next move was a mistake, sets the position up to solve; Main line, at any
// position once the opening is complete, on the game or on a line of the
// reader's own, plays what the board's green arrow shows, best move after
// best move (the shortlist's live reading of each position, the scan's
// until it is in), so it works from any position, a line of one's own or an
// unscanned game included, and never contradicts the arrow. Pure: whether
// the board's position offers each; the panel does the playing.
import { isExploring, moverOf, type ReviewState } from '@chess/api-review';

export interface MistakeAhead {
  /** The move's 1-based ply: the board shows the position before it. */
  ply: number;
  kind: 'blunder' | 'miss';
  san: string;
}

/**
 * The reader's blunder or miss played from the board's position, by the
 * scan's judgement, when the board shows the game itself (not a line, not a
 * position looked back at in a puzzle); undefined otherwise.
 */
export function mistakeAhead(state: ReviewState): MistakeAhead | undefined {
  const { game, scan, reader } = state;
  if (state.status !== 'ready' || !game || !scan || !reader || state.exploring || state.browse !== undefined) return undefined;
  const ply = state.ply + 1;
  const move = game.moves[state.ply];
  if (!move || moverOf(ply) !== reader) return undefined;
  const kind = scan.kind(ply, move, reader);
  return kind === 'blunder' || kind === 'miss' ? { ply, kind, san: move.san } : undefined;
}

/**
 * Whether Main line may be offered: a game is open, the opening is complete
 * at the board's position (the game's ply, or the line's end), and the board
 * is not looking back at a position in a puzzle. Nothing else is needed:
 * the arrow is read from the position itself.
 */
export function mainLineAvailable(state: ReviewState): boolean {
  if (state.status !== 'ready' || !state.game || !state.stages || state.browse !== undefined) return false;
  const at = isExploring(state) ? state.exploring!.fromPly + state.exploring!.moves.length : state.ply;
  return at >= state.stages.openingEnd;
}

/** How many plies Main line plays before it stops on its own: four moves each side, or a mate sooner. */
export const MAIN_LINE_PLIES = 8;
